import React, { useEffect, useRef, useState } from 'react';
import { Anchor, Box, Button, Group, Loader, Select, Text, TextInput, ActionIcon, Tooltip } from '@mantine/core';
import {
  IconStack2,
  IconCheck,
  IconPlus,
  IconTrash,
  IconWorld,
  IconAlertTriangle,
  IconQuestionMark,
  IconCode,
  IconChevronUp,
  IconCircleCheck,
  IconCircleX,
  IconRadar2,
  IconExternalLink,
  IconShoppingCart,
  IconRefresh,
} from '@tabler/icons-react';
import {
  StepProps,
  SubdomainRow,
  DOMAIN_TYPES,
  DOMAIN_TYPE_HINTS,
  DOMAIN_TYPE_SPECIAL,
  platformToFramework,
  frameworkToPlatform,
  domainHost,
} from '../types';
import { SMART_CODE_GUIDE as DOMAIN_TYPE_GUIDE } from '../../../components/GeneralComponent/smartCodeGuides';
import {
  useUniversalSmartCode,
  UniversalSmartCodeContent,
} from '../../../components/GeneralComponent/UniversalSmartCodeContent';
import SettingsApi from '../../../api/SettingsApi';
import { useOnboardingStyles } from '../onboardingStyles';
import { useOnbColors } from '../onboardingColors';
import { StepVideo, notify, startShopifyConnect } from '../common';
import pageStyles from '../Onboarding.module.css';

const SUB_RE = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;
const platformLabel = (v: string) => DOMAIN_TYPES.find((d) => d.value === v)?.label || v;

// Host only — lowercase, no scheme/path/port. Mirrors normalize_domain_host() on the server.
const cleanHost = (v: string) =>
  (v || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split(/[/?#]/)[0]
    .replace(/:\d+$/, '')
    .replace(/\.+$/, '');

// Real per-subdomain Smart Code + verification, mirroring the main-domain step.
function SubdomainSmartCode({
  websiteId,
  row,
  onRowChange,
}: {
  websiteId: string;
  row: SubdomainRow;
  onRowChange: (patch: Partial<SubdomainRow>) => void;
}) {
  const { classes } = useOnboardingStyles();
  const col = useOnbColors();
  const ghlCssRef = useRef(null);
  const ghlSmartCodeRef = useRef(null);
  const footerCodeRef = useRef(null);
  const [verify, setVerify] = useState<'idle' | 'running' | 'success' | 'failed'>('idle');
  const [shopifyBusy, setShopifyBusy] = useState(false);
  // Install opened in a new tab -- show the "return & refresh" panel until connected
  const [installInitiated, setInstallInitiated] = useState(false);
  // A manual Refresh returned "still not connected" -- show the friendly retry message
  const [refreshNotConnected, setRefreshNotConnected] = useState(false);
  // Button spinner for the Refresh Connection action
  const [refreshing, setRefreshing] = useState(false);
  const mounted = useRef(true);
  const isShopify = row.platform === 'shopify';

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Re-read this row's Shopify connection (covers the return from the App Store)
  useEffect(() => {
    if (!isShopify || !row.id) return;
    SettingsApi.getsingledomainsubdomain({ domain_subdomain_id: row.id })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current || !res || res.status !== 1 || !res.data) return;
        onRowChange({
          shopifyConnected: Number(res.data.is_shopify_connected) === 1,
          shopifyStore: res.data.shopify_store_name || '',
        });
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [isShopify, row.id]);

  const connectShopify = () => {
    const opened = startShopifyConnect(row.id, 'sub', `/onboarding${window.location.search}`);
    if (opened) {
      setInstallInitiated(true);
      setRefreshNotConnected(false);
    }
  };

  // Re-read this subdomain's connection after the install tab round-trip.
  // mode 'manual' (Refresh button) shows the spinner + not-connected message on failure;
  // 'silent' (tab-focus auto-check) only promotes to connected without flicker.
  const verifyShopify = (mode: 'manual' | 'silent' = 'manual') => {
    if (!row.id) return;
    if (mode === 'manual') { setRefreshing(true); setRefreshNotConnected(false); }
    SettingsApi.getsingledomainsubdomain({ domain_subdomain_id: row.id })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        if (mode === 'manual') setRefreshing(false);
        const connected = Boolean(res && res.status === 1 && res.data && Number(res.data.is_shopify_connected) === 1);
        if (connected) {
          onRowChange({ shopifyConnected: true, shopifyStore: (res.data && res.data.shopify_store_name) || '' });
          setRefreshNotConnected(false);
        } else if (mode === 'manual') {
          setRefreshNotConnected(true);
        }
      })
      .catch(() => {
        if (!mounted.current) return;
        if (mode === 'manual') { setRefreshing(false); setRefreshNotConnected(true); }
      });
  };

  // After the install tab is opened, silently re-verify when the user returns to this tab,
  // so a completed install flips to "Connected" without them having to click Refresh.
  useEffect(() => {
    if (!isShopify || !installInitiated || row.shopifyConnected) return;
    const onFocus = () => verifyShopify('silent');
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line
  }, [isShopify, installInitiated, row.shopifyConnected, row.id]);

  const disconnectShopify = () => {
    setShopifyBusy(true);
    SettingsApi.disconnectshopify({ domain_subdomain_id: row.id })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        setShopifyBusy(false);
        if (res && res.status === 1) {
          onRowChange({ shopifyConnected: false, shopifyStore: '' });
          setVerify('idle');
          setInstallInitiated(false);
          setRefreshNotConnected(false);
          notify('success', `${row.name} disconnected from Shopify.`, 'Disconnected');
        } else {
          notify('error', (res && res.message) || 'Could not disconnect the store.', 'Error');
        }
      })
      .catch(() => {
        if (!mounted.current) return;
        setShopifyBusy(false);
        notify('error', 'Could not disconnect the store.', 'Error');
      });
  };

  const { data: smartCodeData, loading } = useUniversalSmartCode({
    websiteId,
    isSubdomain: true,
    subdomainId: row.id,
    enabled: Boolean(websiteId && row.id),
  });

  const runVerify = () => {
    setVerify('running');
    SettingsApi.verifysmartcode({ website_id: websiteId, subdomain_id: row.id })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        setVerify(res && res.status === 1 && res.data && res.data.detected ? 'success' : 'failed');
      })
      .catch(() => mounted.current && setVerify('failed'));
  };

  const special = DOMAIN_TYPE_SPECIAL[row.platform];
  const guideUrl = DOMAIN_TYPE_GUIDE[row.platform] || DOMAIN_TYPE_GUIDE.custom;

  return (
    <Box className={pageStyles.fadeIn} style={{ display: 'grid', gap: 12 }}>
      <div className={classes.hintBox}>{DOMAIN_TYPE_HINTS[row.platform] || DOMAIN_TYPE_HINTS.custom}</div>

      {!isShopify && special && special.length > 0 && (
        <div className={classes.specialBox}>
          <div className={classes.noteHeading} style={{ color: col.purple }}>
            <IconAlertTriangle size={15} /> Special instructions for {platformLabel(row.platform)}
          </div>
          {special.map((s, i) => (
            <div key={i} className={classes.bulletRow}>
              <IconCheck size={15} color={col.purpleText} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{s}</span>
            </div>
          ))}
        </div>
      )}

      <Group position="right" align="center" noWrap style={{ width: '100%' }}>
        <Anchor href={guideUrl} target="_blank" rel="noreferrer" fz={13.5} fw={600} c={col.purple}>
          <Group spacing={6} noWrap>
            <IconExternalLink size={15} /> How to install on {platformLabel(row.platform)}
          </Group>
        </Anchor>
      </Group>

      {/* Shopify: the Public App installs the Smart Code — no manual paste */}
      {isShopify ? (
        row.shopifyConnected ? (
          <div className={classes.detectCard} style={{ borderColor: col.greenBorder, background: col.greenBg }}>
            <div className={classes.detectIcon} style={{ background: col.greenBg, color: col.green }}>
              <IconCircleCheck size={20} />
            </div>
            <Box style={{ flex: 1 }}>
              <Text fw={700} fz="sm" c={col.greenDeep}>
                Shopify store connected
              </Text>
              <Text fz={13} c={col.greenText}>
                {row.shopifyStore || row.name} — the Visiopt app installs the Smart Code for you.
              </Text>
            </Box>
            <Group spacing={6} noWrap>
              <Button radius="md" variant="default" disabled={shopifyBusy} onClick={connectShopify}>
                Reconnect
              </Button>
              <Button radius="md" variant="subtle" color="red" loading={shopifyBusy} onClick={disconnectShopify}>
                Disconnect
              </Button>
            </Group>
          </div>
        ) : !installInitiated ? (
          <>
            <div className={classes.stepNote}>
              <div className={classes.noteHeading}>Connect this Shopify store</div>
              <ol style={{ margin: '4px 0 0', paddingLeft: 20, color: col.textSecondary, fontSize: 13.5, lineHeight: 1.7 }}>
                <li>Click <b>Connect Shopify Store</b> — Shopify opens in a <b>new tab</b> to install the Visiopt app.</li>
                <li>Approve the install for <b>{row.name}</b>.</li>
                <li>In the theme editor, turn ON the <b>Visiopt Integration</b> toggle and click <b>Save</b>.</li>
                <li>Return to this tab and click <b>Refresh Connection</b> to verify.</li>
              </ol>
            </div>
            <Button
              className={classes.blackBtn}
              radius="md"
              fullWidth
              leftIcon={<IconShoppingCart size={18} />}
              onClick={connectShopify}
            >
              Connect Shopify Store
            </Button>
          </>
        ) : (
          <>
            <div className={classes.stepNote} style={{ borderColor: col.amberBorder, background: col.amberBg }}>
              <Group spacing={9} noWrap align="flex-start">
                <IconExternalLink size={18} color={col.amber} style={{ flexShrink: 0, marginTop: 2 }} />
                <Box>
                  <div className={classes.noteHeading}>Complete the Shopify installation in the new tab</div>
                  <Text fz={13.5} c={col.textSecondary} mt={4} style={{ lineHeight: 1.7 }}>
                    We opened Shopify in a new browser tab. Approve the Visiopt app install for <b>{row.name}</b>, then return to this tab and click <b>Refresh Connection</b> to verify your store connection.
                  </Text>
                </Box>
              </Group>
            </div>

            {refreshNotConnected && (
              <div className={classes.stepNote} style={{ borderColor: col.amberBorder2, background: col.amberBg2, marginTop: 10 }}>
                <Group spacing={8} noWrap align="flex-start">
                  <IconAlertTriangle size={16} color={col.amber} style={{ flexShrink: 0, marginTop: 2 }} />
                  <Text fz={13} c={col.amberText} style={{ lineHeight: 1.6 }}>
                    <b>Shopify is not connected yet.</b> Please complete the app installation in the other tab, then return here and click <b>Refresh Connection</b> again.
                  </Text>
                </Group>
              </div>
            )}

            <Group mt={12} spacing={10} noWrap grow>
              <Button
                className={classes.blackBtn}
                radius="md"
                leftIcon={<IconRefresh size={18} />}
                loading={refreshing}
                onClick={() => verifyShopify('manual')}
              >
                Refresh Connection
              </Button>
              <Button
                variant="default"
                radius="md"
                leftIcon={<IconShoppingCart size={16} />}
                onClick={connectShopify}
              >
                Reopen Shopify
              </Button>
            </Group>
          </>
        )
      ) : loading ? (
        <Group position="center" py={20}>
          <Loader size="sm" color="grape" />
        </Group>
      ) : (
        <UniversalSmartCodeContent
          data={smartCodeData}
          ghlCssref={ghlCssRef}
          ghlSmartCodeRef={ghlSmartCodeRef}
          footerCodeRef={footerCodeRef}
          handleHelpClick={() => {}}
        />
      )}

      {verify === 'success' && (
        <Group className={classes.verifyOk} spacing={10} noWrap>
          <IconCircleCheck size={20} color={col.green} />
          <Text fz={13} fw={600} c={col.greenDeep} style={{ flex: 1 }}>
            Smart Code detected on {row.name}.
          </Text>
          <Button size="xs" radius="md" variant="subtle" color="gray" onClick={runVerify}>
            Re-check
          </Button>
        </Group>
      )}

      {verify === 'failed' && (
        <div className={classes.failBox}>
          <div className={classes.noteHeading} style={{ color: col.red }}>
            <IconCircleX size={16} /> Smart Code Not Detected Yet
          </div>
          <Text fz={13} c={col.redDeep}>
            Publish your changes, clear any cache/CDN, wait a couple of minutes, then check again. This step is optional —
            you can continue and Visiopt will keep checking in the background.
          </Text>
        </div>
      )}

      {/* For Shopify the verify button only makes sense once the store is connected */}
      {verify !== 'success' && (!isShopify || row.shopifyConnected) && (
        <Button
          className={classes.blackBtn}
          radius="md"
          fullWidth
          loading={verify === 'running'}
          leftIcon={verify !== 'running' ? <IconRadar2 size={16} /> : undefined}
          onClick={runVerify}
        >
          {verify === 'failed' ? 'Check Again' : isShopify ? 'Verify Tracking' : "I've Installed the Smart Code"}
        </Button>
      )}
    </Box>
  );
}

const EXAMPLES = [
  'Landing page domains',
  'Checkout domains',
  'Funnel domains',
  'Membership sites',
  'Booking pages',
  'Subdomains',
  'Third-party checkout domains',
];

export function Step3Subdomains({ data, update }: StepProps) {
  const { classes } = useOnboardingStyles();
  const col = useOnbColors();
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const mounted = useRef(true);
  const websiteId = data.domain.website_id;

  const rows = data.subdomains;

  // Load existing related domains from the server on mount (resume-safe)
  useEffect(() => {
    mounted.current = true;
    if (websiteId && rows.length === 0) {
      SettingsApi.getdomainsubdomainlist({ website_id: websiteId })
        .then((r: Response) => (r.ok ? r.json() : null))
        .then((res: any) => {
          if (!mounted.current || !res || res.status !== 1 || !Array.isArray(res.data)) return;
          const loaded: SubdomainRow[] = res.data.map((d: any) => ({
            id: d.domain_subdomain_id,
            name: d.domain_name,
            platform: frameworkToPlatform(d.subdomain_type, d.subdomain_frontend_framework),
            shopifyConnected: Number(d.is_shopify_connected) === 1,
            shopifyStore: d.shopify_store_name || '',
          }));
          update({ subdomains: loaded });
        })
        .catch(() => {
          if (mounted.current) notify('error', 'Could not load your related domains. Please refresh.', 'Error');
        });
    }
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line
  }, []);

  // Add a subdomain → detect its platform, save it to the website, then show its Smart Code
  const addManual = () => {
    const clean = cleanHost(input);
    if (!clean) return setError('Enter a domain, e.g. checkout.mysite.com');
    if (!SUB_RE.test(clean)) return setError('Enter a valid domain, e.g. checkout.mysite.com');
    if (clean.replace(/^www\./, '') === domainHost(data.domain.url)) {
      return setError('This is already your main website domain.');
    }
    if (rows.some((r) => cleanHost(r.name) === clean)) return setError('That domain is already added.');
    if (!websiteId) return setError('Please complete step 1 first.');
    setError('');
    setAdding(true);

    // 1) detect the subdomain's platform (falls back to the main domain's platform)
    SettingsApi.detectplatform({ website_address: clean })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        const detectedPlat =
          res && res.status === 1 && res.data && res.data.platform ? res.data.platform : data.domain.type || 'custom';
        const fw = platformToFramework(detectedPlat);
        // 2) persist the subdomain against the website
        return SettingsApi.adddeletedomainsubdomain({
          action_perform: 'save',
          website_id: websiteId,
          domain_name: clean,
          domain_type: fw.front_end_framework,
          domain_frontend_framwork: fw.framework_type,
        })
          .then((r2: Response) => (r2.ok ? r2.json() : null))
          .then((saveRes: any) => ({ saveRes, detectedPlat }));
      })
      .then((out: any) => {
        if (!mounted.current) return;
        const { saveRes, detectedPlat } = out || {};
        if (saveRes && saveRes.status === 1 && saveRes.data && saveRes.data.website_sub_domain_id) {
          const newRow: SubdomainRow = { id: saveRes.data.website_sub_domain_id, name: clean, platform: detectedPlat };
          update({ subdomains: [...rows, newRow] });
          setExpandedId(newRow.id);
          notify('success', `${clean} added as ${platformLabel(detectedPlat)}.`, 'Related domain added');
          setInput('');
        } else {
          notify('error', (saveRes && saveRes.message) || 'Could not add the related domain.', 'Error');
        }
        setAdding(false);
      })
      .catch(() => {
        if (!mounted.current) return;
        setAdding(false);
        notify('error', 'Could not add the related domain. Please try again.', 'Error');
      });
  };

  // Remove a subdomain from the website
  const removeRow = (id: string) => {
    setRemovingId(id);
    SettingsApi.adddeletedomainsubdomain({ action_perform: 'delete', domain_subdomain_id: id })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        if (res && res.status === 1) {
          update({ subdomains: rows.filter((r) => r.id !== id) });
        } else {
          notify('error', (res && res.message) || 'Could not remove the related domain.', 'Error');
        }
        setRemovingId(null);
      })
      .catch(() => {
        if (!mounted.current) return;
        setRemovingId(null);
        notify('error', 'Could not remove the related domain. Please try again.', 'Error');
      });
  };

  // Change a subdomain's platform → update the website record
  // Optimistic platform change, reverted if the server rejects it (e.g. a connected
  // Shopify store must be disconnected before its type can change).
  const updateRowPlatform = (id: string, plat: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    const fw = platformToFramework(plat);
    const revert = () => update({ subdomains: rows.map((r) => (r.id === id ? row : r)) });
    update({
      subdomains: rows.map((r) => (r.id === id ? { ...r, platform: plat, shopifyConnected: false, shopifyStore: '' } : r)),
    });
    SettingsApi.adddeletedomainsubdomain({
      action_perform: 'update',
      domain_subdomain_id: id,
      domain_name: row.name,
      domain_type: fw.front_end_framework,
      domain_frontend_framwork: fw.framework_type,
    })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        if (!res || res.status !== 1) {
          revert();
          notify('error', (res && res.message) || 'Could not update the platform.', 'Error');
        }
      })
      .catch(() => {
        if (!mounted.current) return;
        revert();
        notify('error', 'Could not update the platform. Please try again.', 'Error');
      });
  };

  return (
    <Box className={`${classes.inner} ${pageStyles.stepEnter}`} style={{ maxWidth: 700 }}>
      <div className={classes.mark}>
        <IconStack2 size={28} />
      </div>
      <h1 className={classes.title}>
        Add Related Domains <span className={classes.optionalTag}>(Optional)</span>
      </h1>
      <p className={classes.subtitle}>
        Add any domains or subdomains that are part of the same website or funnel. Visiopt will treat
        them as one visitor journey for seamless cross-domain tracking.
      </p>

      <StepVideo
        duration="0:50"
        sublabel="A 50-second walkthrough of this step"
        modalTitle="How to add related domains"
        videoUrl="https://vimeo.com/1158866937"
      />

      <div className={classes.fields}>
        {/* Manual add */}
        <TextInput
          size="md"
          radius="md"
          label="Domain or Subdomain"
          classNames={{ label: classes.fieldLabel }}
          placeholder="Example: checkout.mysite.com"
          icon={<IconWorld size={18} />}
          value={input}
          error={error}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && addManual()}
        />
        <Button
          className={classes.blackBtn}
          radius="md"
          fullWidth
          loading={adding}
          leftIcon={!adding ? <IconPlus size={16} /> : undefined}
          onClick={addManual}
        >
          {adding ? 'Detecting platform…' : 'Add Related Domain'}
        </Button>

        {/* Added subdomains — each gets its own detected platform + Smart Code instructions */}
        {rows.map((r: SubdomainRow) => {
          return (
            <div
              key={r.id}
              className={`${classes.discBox} ${pageStyles.fadeIn}`}
              style={{ padding: 14, display: 'grid', gap: 12, textAlign: 'left' }}
            >
              {/* Header — name + detected platform + remove */}
              <Group position="apart" noWrap align="flex-start">
                <Group spacing={10} noWrap style={{ minWidth: 0 }}>
                  <IconWorld size={16} color={col.textPrimary} />
                  <Box style={{ minWidth: 0 }}>
                    <Text fz="sm" fw={700} c={col.textPrimary} truncate>
                      {r.name}
                    </Text>
                    <Group spacing={8} noWrap>
                      <Text fz={12} c={col.green} fw={600}>
                        Detected: {platformLabel(r.platform)}
                      </Text>
                      <Anchor fz={12} fw={600} c={col.purple} onClick={() => setEditingId(editingId === r.id ? null : r.id)}>
                        Change
                      </Anchor>
                    </Group>
                  </Box>
                </Group>
                <Group spacing={4} noWrap>
                  <Tooltip label={expandedId === r.id ? 'Hide Smart Code' : 'View Smart Code'} withArrow>
                    <ActionIcon
                      variant="light"
                      color="grape"
                      radius="md"
                      onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                    >
                      {expandedId === r.id ? <IconChevronUp size={16} /> : <IconCode size={16} />}
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Remove" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      radius="md"
                      loading={removingId === r.id}
                      onClick={() => removeRow(r.id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>

              {/* Change platform */}
              {editingId === r.id && (
                <Select
                  size="sm"
                  radius="md"
                  searchable
                  placeholder="Choose platform"
                  data={DOMAIN_TYPES}
                  value={r.platform || null}
                  onChange={(v) => {
                    updateRowPlatform(r.id, v || 'custom');
                    setEditingId(null);
                  }}
                />
              )}

              {/* Real per-subdomain Smart Code + verification — shown when expanded */}
              {expandedId === r.id && (
                <SubdomainSmartCode
                  websiteId={websiteId}
                  row={r}
                  onRowChange={(patch) =>
                    update({ subdomains: rows.map((x) => (x.id === r.id ? { ...x, ...patch } : x)) })
                  }
                />
              )}
            </div>
          );
        })}

        {/* Info box */}
        <div className={classes.qaBox}>
          <div className={classes.qaHead}>
            <span className={classes.qaDot}>
              <IconQuestionMark size={13} />
            </span>
            When should I add related domains?
          </div>
          <Text className={classes.qaText}>
            Related domains are any websites or subdomains that visitors may navigate between during
            the same customer journey.
          </Text>
          <div className={classes.qaSubhead}>Examples include:</div>
          <div className={classes.qaList}>
            {EXAMPLES.map((e) => (
              <div key={e} className={classes.qaItem}>
                <span className={classes.qaBullet} />
                {e}
              </div>
            ))}
          </div>
          <Text className={classes.qaNote}>
            Adding them now won't affect your setup if you don't use them immediately, but it makes
            future cross-domain tracking much easier.
          </Text>
        </div>

        {/* Important box */}
        <div className={classes.cachingBox}>
          <div className={classes.noteHeading} style={{ color: col.amberText }}>
            <IconAlertTriangle size={15} /> IMPORTANT
          </div>
          <Text fz={13.5} c={col.amberText}>
            This step is only for domains that are part of the same website or funnel. If you want to
            track a completely separate website in the future, you can add it anytime from your Visiopt
            dashboard.
          </Text>
        </div>
      </div>
    </Box>
  );
}
